import { format, isSameMonth } from 'date-fns';
import { DATE_FORMAT_ISO_DAY } from '../../../constants/dateFormat';
import { CALENDAR_WEEKDAY_HEADER_LABELS, monthGridDays } from '../functions';
import { EMPTY_PUBLICATIONS } from '../calendarCellAnchor';
import type { CalendarDayLabel } from '../calendarTemporalLabels';
import type { CalendarPublication, CalendarPublicationHoverAnchor } from '../types';
import { MonthDayCell } from './MonthDayCell';

const DAYS_PER_WEEK = 7;

const CONTAINER_CLASS = 'rounded-md border border-light-gray';
const HEADER_ROW_CLASS = 'grid min-w-[720px] grid-cols-7 bg-primary-light';
const HEADER_CELL_CLASS =
    'border-r border-light-gray last:border-r-0 p-2 text-center text-more-small font-medium uppercase text-gray';
const BODY_GRID_CLASS = 'grid min-w-[720px] grid-cols-7';

type MonthCalendarViewProps = Readonly<{
    anchorDate: Date;
    byDay: ReadonlyMap<string, readonly CalendarPublication[]>;
    dayLabels: Readonly<Record<string, CalendarDayLabel>>;
    onPublicationHoverStart: (
        publication: CalendarPublication,
        anchor: CalendarPublicationHoverAnchor,
    ) => void;
    onPublicationHoverEnd: () => void;
    onOpenDayList: (day: Date, top: number, left: number) => void;
}>;

export function MonthCalendarView({
    anchorDate,
    byDay,
    dayLabels,
    onPublicationHoverStart,
    onPublicationHoverEnd,
    onOpenDayList,
}: MonthCalendarViewProps) {
    const days = monthGridDays(anchorDate);
    const lastIndex = days.length - 1;

    return (
        <div className={CONTAINER_CLASS}>
            <div className={HEADER_ROW_CLASS}>
                {CALENDAR_WEEKDAY_HEADER_LABELS.map(label => (
                    <div key={label} className={HEADER_CELL_CLASS}>
                        {label}
                    </div>
                ))}
            </div>
            <div className={BODY_GRID_CLASS}>
                {days.map((day, index) => {
                    const key = format(day, DATE_FORMAT_ISO_DAY);
                    const dayNumberLabel = dayLabels[key]?.day_of_month ?? format(day, 'd');
                    return (
                        <MonthDayCell
                            key={key}
                            day={day}
                            dayKey={key}
                            dayNumberLabel={dayNumberLabel}
                            publications={byDay.get(key) ?? EMPTY_PUBLICATIONS}
                            inMonth={isSameMonth(day, anchorDate)}
                            isLastCol={(index + 1) % DAYS_PER_WEEK === 0}
                            isLastRow={index > lastIndex - DAYS_PER_WEEK}
                            onPublicationHoverStart={onPublicationHoverStart}
                            onPublicationHoverEnd={onPublicationHoverEnd}
                            onOpenDayList={onOpenDayList}
                        />
                    );
                })}
            </div>
        </div>
    );
}
